import React, { useCallback, useMemo, useRef, useState } from "react";
import { formatUSD, formatPercent } from "@/utils/formatters";
import { useAuth } from "../context/AuthContext";
import { useData } from "../hooks/useData";
import { useRBAC } from "../hooks/useRBAC";
import { useLeaderboard } from "../hooks/data-fetching";
import { usePersistedState } from "../hooks/usePersistedState";
import { useGlobalDateRange } from "../hooks/useGlobalDateRange";
import { LeaderboardEntry } from "../types";
import Card from "../components/ui/Card";

import DateRangePicker from "../components/heatmap/DateRangePicker";
import MetricInfoIcon from "../components/ui/MetricInfoIcon";
import { PrestigeBadge } from "../components/prestige/PrestigeBadge";
import CalculationsDrawer from "../components/leaderboard/CalculationsDrawer";
import ReceiptsPopover from "../components/leaderboard/ReceiptsPopover";
import ReceiptsModal from "../components/leaderboard/ReceiptsModal";
import { useLeaderboardReceipts } from "../hooks/data-fetching/useLeaderboardReceipts";
import type { LeaderboardMetric, LeaderboardReceiptsMap } from "../types";
import FormulaGuide from "../components/leaderboard/FormulaGuide";
import RepBreakdownPanel from "../components/leaderboard/RepBreakdownPanel";
import { sectorCPC } from "../components/leaderboard/scoreBreakdownUtils";
import { MIN_VOLUME_GATE } from "../components/leaderboard/constants";
import MvpVotingModal from "../components/prestige/MvpVotingModal";
import MvpAdminModal from "../components/prestige/MvpAdminModal";
import { useMvpVoting } from "../hooks/data-fetching/useMvpVoting";
import { useDialerScorecards } from "../hooks/useDialerScorecards";
import type { PrestigeRank } from "../types";
import type { MetricSample } from "../hooks/useMetricSamples";
import { HEX } from "../lib/colorSystem";
import { toOrgDateKey, formatDateKeyDisplay, timestampToOrgDateKey } from "../lib/timezone";

// =============================================================================
// LEADERBOARD PAGE — V2 REDESIGN
// =============================================================================
// STRUCTURAL CHANGES from V1:
// - Sectors are TABBED (horizontal slide animation), not stacked vertically
// - SVG podium for top 3 reps above the table
// - Asymmetric hero banner (2fr/1fr/1fr) with dominant rank card
// - Progressive row hierarchy: #1 tallest/boldest → #4+ compact
// - Score bars showing relative performance vs #1
// - Typography cascade: DM Mono numbers, Plus Jakarta Sans titles
// - Color consolidation: Gold/Silver/Bronze rank tier + Status (green/red/amber)
// - Empty state collapse: slim 48px bar instead of full-height centered icon
//
// UNCHANGED:
// - RBAC (hasTunnelVision, canViewAllPods)
// - Data flow (useLeaderboard, Realtime, computeWeightedRanking)
// - Pod/Company-Wide toggle, Daily/Range toggle
// - Prestige standings (company-wide view)
// =============================================================================

type SectorFilter = "closer" | "setter" | "dialer";
type ViewTab = "pod" | "company";

const SECTOR_LABELS: Record<SectorFilter, string> = {
  closer: "Closers",
  setter: "Setters",
  dialer: "Dialers",
};
const COMPANY_WIDE_TOP_N = 50;

// Rank tier colors — muted, earthy, not candy
const RANK_COLORS = {
  gold: { bg: "rgba(212, 168, 67, 0.10)", accent: "#D4A843", text: "#D4A843" },
  silver: { bg: "rgba(160, 165, 173, 0.10)", accent: "#A0A5AD", text: "#A0A5AD" },
  bronze: { bg: "rgba(192, 138, 83, 0.10)", accent: "#C08A53", text: "#C08A53" },
};

// formatPercent imported from utils/formatters — expects 0-100 range
// Leaderboard values are 0-1 ratios, so multiply by 100 at call sites

// sectorCPC moved to components/leaderboard/scoreBreakdownUtils (2026-07-04)
// so the inline RepBreakdownPanel shares the exact ranking CPC definition.

// P0g (May 2, 2026): Min-volume gate for range-mode ranking. Reps below threshold
// don't compete for rank — keeps a 1-call-booked rep with $7,500 CPC from rocketing
// to #1 over a setter who booked 49 calls. Per Nathaniel's spec
// (REF_nathaniel_leaderboard_prestige_kpi_source.md:134), quality should beat raw
// volume — but only when both reps have a real sample.
//
// Daily ranking (sql/leaderboard/calculate_rankings.sql) is intentionally NOT gated.
// A daily threshold would empty the leaderboard on light-volume days. The degenerate
// edge only appears in aggregate/range mode where reps have varying day counts.
//
// Gate values live in components/leaderboard/constants.ts (MIN_VOLUME_GATE) — SSOT
// shared with scoreBreakdownUtils.ts / RepBreakdownPanel.tsx / FormulaGuide.tsx.
// Previously re-declared here as MIN_VOLUME_FOR_RANK; deduped to avoid silent drift.

// P0-LBTOTAL (2026-07-03): calendar months whose first AND last day both fall
// inside [startKey, endKey] (org-date-key strings "YYYY-MM-DD" — ISO string
// comparison is safe). MVP is a month-end award, so partial months are
// excluded. Returns month-start strings "YYYY-MM-01" matching how PostgREST
// serializes prestige_points.month — compare by string equality.
const coveredMonths = (startKey: string, endKey: string): string[] => {
  if (!startKey || !endKey || endKey === "9999-12-31") return [];
  const months: string[] = [];
  let y = Number(startKey.slice(0, 4));
  let m = Number(startKey.slice(5, 7));
  const ey = Number(endKey.slice(0, 4));
  const em = Number(endKey.slice(5, 7));
  if (!y || !m || !ey || !em) return [];
  while (y < ey || (y === ey && m <= em)) {
    const mm = String(m).padStart(2, "0");
    const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const first = `${y}-${mm}-01`;
    const last = `${y}-${mm}-${String(lastDay).padStart(2, "0")}`;
    if (startKey <= first && endKey >= last) months.push(first);
    if (m === 12) {
      y += 1;
      m = 1;
    } else {
      m += 1;
    }
  }
  return months;
};

const computeWeightedRanking = (
  entries: LeaderboardEntry[],
  sector: SectorFilter
): LeaderboardEntry[] => {
  if (entries.length === 0) return [];
  const withCPC = entries.map((e) => ({ ...e, cpc: sectorCPC(e, sector) }));

  // Split qualified vs unqualified by sector-specific volume threshold
  const gate = MIN_VOLUME_GATE[sector];
  const qualified = withCPC.filter(
    (e) => Number(e[gate.field as keyof LeaderboardEntry] ?? 0) >= gate.min
  );
  const unqualified = withCPC.filter(
    (e) => Number(e[gate.field as keyof LeaderboardEntry] ?? 0) < gate.min
  );

  // Normalize and rank ONLY qualified reps so a 1-call rep can't dominate the maxes
  const safeMax = (vals: number[]) => Math.max(...vals, 0.001);
  const maxCC = safeMax(qualified.map((e) => e.cc_total || 0));
  const maxSR = safeMax(qualified.map((e) => e.show_rate || 0));
  const maxCPC = safeMax(qualified.map((e) => e.cpc));
  const maxCB = safeMax(qualified.map((e) => e.calls_booked || 0));

  const ranked = qualified
    .map((entry) => {
      const srN = (entry.show_rate || 0) / maxSR;
      const cpcN = entry.cpc / maxCPC;
      let rank_score: number;
      if (sector === "closer") {
        rank_score = ((entry.cc_total || 0) / maxCC) * 0.5 + srN * 0.25 + cpcN * 0.25;
      } else {
        // setter + dialer share one formula (dialer mirrored the setter
        // 2026-06-02, Session 25): CPC 40 / Show Rate 30 / Booked 30.
        rank_score = cpcN * 0.4 + srN * 0.3 + ((entry.calls_booked || 0) / maxCB) * 0.3;
      }
      return { ...entry, rank_score };
    })
    // Stage B (Session 25): rank by points-in-window (§3.7 winner rule), not the
    // rank_score KPI composite. rank_score stays computed for the tiebreak + hover.
    // P0-LBTOTAL (2026-07-03): points-in-window is now the PRESTIGE TOTAL
    // (lb + kpi + mvp, REF:370-374) when the aggregation computed it; bare
    // points_earned is the day-mode/legacy fallback.
    // KNOWN BOUNDARY (pre-existing): useLeaderboard caps daily rows at 1000
    // (newest-first), while the kpi delta RPC sums the FULL window — on very
    // long ranges lb points can be partial while kpi is complete. Typical
    // month views (≤ ~600 rows) are unaffected. Real fix = Part B live RPC.
    .sort(
      (a, b) =>
        (b.prestige_total ?? b.points_earned ?? 0) - (a.prestige_total ?? a.points_earned ?? 0) ||
        b.rank_score - a.rank_score
    )
    .map((entry, i) => ({ ...entry, rank: i + 1 }));

  // Unqualified reps appear AFTER qualified ones with rank=null, rank_score=null.
  // The table renderer already handles null rank with "—" display.
  // Sorted by their volume-gate field DESC so the closest-to-qualifying shows first.
  const unranked = unqualified
    .map((entry) => ({ ...entry, rank_score: null as number | null, rank: 0 }))
    .sort(
      (a, b) =>
        Number(b[gate.field as keyof LeaderboardEntry] ?? 0) -
        Number(a[gate.field as keyof LeaderboardEntry] ?? 0)
    );

  return [...ranked, ...unranked] as LeaderboardEntry[];
};

// --- Podium SVG Component ---
const Podium: React.FC<{
  entries: LeaderboardEntry[];
  sector: SectorFilter;
  prestigeRanks: PrestigeRank[];
  dialsById?: Map<string, number>;
}> = ({ entries, sector, prestigeRanks, dialsById }) => {
  const top3 = entries.slice(0, 3);
  if (top3.length === 0) return null;

  // Podium order: #2 left, #1 center, #3 right
  const ordered = [top3[1], top3[0], top3[2]].filter(Boolean);
  // Stage B (Session 25): pedestal height tracks points (Option B), same as the
  // table ScoreBar — so a 1st-place pedestal is never shorter than the 2nd beside it.
  // P0-LBTOTAL: prestige_total when present (range mode); Math.max guards the
  // all-negative sector (|| 1 would keep a negative max and flip every ratio).
  const maxScore = Math.max(top3[0]?.prestige_total ?? top3[0]?.points_earned ?? 0, 1);

  // Pedestal heights proportional to points (ratio clamped ≥ 0 — negative
  // totals are legal and render as min-height pedestals)
  const getHeight = (entry: LeaderboardEntry) => {
    const ratio = Math.max((entry.prestige_total ?? entry.points_earned ?? 0) / maxScore, 0);
    return Math.max(40, Math.round(ratio * 100)); // 40-100px range
  };

  const pedestalColors = [RANK_COLORS.silver, RANK_COLORS.gold, RANK_COLORS.bronze];
  const pedestalLabels = ["2nd", "1st", "3rd"];
  const ranks = [2, 1, 3];

  // Key metric per sector
  const keyMetric = (entry: LeaderboardEntry) => {
    if (sector === "closer") return formatUSD(entry.cc_total || 0);
    if (sector === "setter") return formatUSD(sectorCPC(entry, sector));
    // daily_leaderboard.daily_dials is always 0 (DialHQ-only writer). Real dials come from
    // get_dialer_scorecards (SA6, 2026-10-06). Unknown (e.g. another org in company view) shows a dash.
    const dials = dialsById?.get(entry.rep_id);
    return dials === undefined ? "\u2014 dials" : `${dials} dials`;
  };

  return (
    <div className="flex items-end justify-center gap-3 py-6">
      {ordered.map((entry, i) => {
        if (!entry) return null;
        const height = getHeight(entry);
        const colors = pedestalColors[i];
        const rank = prestigeRanks.find((r) => r.rep_id === entry.rep_id);
        const isFirst = ranks[i] === 1;

        return (
          <div
            key={entry.rep_id}
            className="flex flex-col items-center gap-2"
            style={{ width: isFirst ? 140 : 120 }}
          >
            {/* Rep info above pedestal */}
            <div className="flex flex-col items-center justify-end gap-1 min-h-[72px]">
              {rank && rank.current_level >= 0 && (
                <PrestigeBadge
                  level={rank.current_level}
                  highestAchieved={rank.highest_level_achieved}
                  size="sm"
                  showStars={false}
                />
              )}
              <span
                className={`font-semibold text-glass text-center leading-tight ${isFirst ? "text-sm" : "text-xs"}`}
                style={{ maxWidth: isFirst ? 140 : 120 }}
              >
                {entry.rep_name}
              </span>
              <span className={`text-glass-secondary ${isFirst ? "token-15" : "token-13m"}`}>
                {keyMetric(entry)}
              </span>
              <span className="font-mono text-glass-muted tabular-nums token-11">
                {entry.prestige_total ?? entry.points_earned ?? 0} pts
              </span>
            </div>

            {/* Pedestal */}
            <div
              className="w-full rounded-t-lg flex items-end justify-center pb-2 transition-all duration-300"
              style={{
                height,
                backgroundColor: colors.bg,
                borderTop: `2px solid ${colors.accent}`,
              }}
            >
              <span className="token-10 uppercase" style={{ color: colors.text }}>
                {pedestalLabels[i]}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- Score Bar (horizontal fill showing relative performance) ---
const ScoreBar: React.FC<{ score: number; maxScore: number; rank: number }> = ({
  score,
  maxScore,
  rank,
}) => {
  // Ratio clamped ≥ 0 — negative prestige totals are legal (P0-LBTOTAL) and
  // render as an empty bar, not a negative width.
  const pct = maxScore > 0 ? Math.max(Math.round((score / maxScore) * 100), 0) : 0;
  const color =
    rank === 1
      ? RANK_COLORS.gold.accent
      : rank === 2
        ? RANK_COLORS.silver.accent
        : rank === 3
          ? RANK_COLORS.bronze.accent
          : "var(--glass-muted-color, rgba(156,163,175,0.4))";

  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="font-mono token-11 tabular-nums text-glass-muted w-8 text-right">
        {pct}%
      </span>
    </div>
  );
};

// --- Rank Badge ---
const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  const colors =
    rank === 1
      ? { bg: RANK_COLORS.gold.bg, text: RANK_COLORS.gold.text }
      : rank === 2
        ? { bg: RANK_COLORS.silver.bg, text: RANK_COLORS.silver.text }
        : rank === 3
          ? { bg: RANK_COLORS.bronze.bg, text: RANK_COLORS.bronze.text }
          : { bg: "transparent", text: "var(--glass-muted-color, #9CA3AF)" };

  return (
    <span
      className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold font-mono tabular-nums"
      style={{ backgroundColor: rank <= 3 ? colors.bg : undefined, color: colors.text }}
    >
      {rank || "--"}
    </span>
  );
};

// --- Point Badge ---
const PointBadge: React.FC<{ rank: number; points: number }> = ({ rank, points }) => {
  if (rank <= 3 && points > 0) {
    const color =
      rank === 1
        ? RANK_COLORS.gold.text
        : rank === 2
          ? RANK_COLORS.silver.text
          : RANK_COLORS.bronze.text;
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full token-11 font-medium font-mono tabular-nums"
        style={{ backgroundColor: `${color}15`, color }}
      >
        {points} pts
      </span>
    );
  }
  if (rank === 4 && points > 0) {
    return (
      <span className="font-mono tabular-nums token-11 text-glass-secondary">{points} pts</span>
    );
  }
  return <span className="font-mono tabular-nums token-11 text-glass-muted">{points || 0}</span>;
};

// --- Sector Table Component (redesigned with progressive rows) ---
const SectorTable: React.FC<{
  sector: SectorFilter;
  entries: LeaderboardEntry[];
  userId?: string;
  isUserSector: boolean;
  getSamples: (key: string) => MetricSample[];
  prestigeRanks: PrestigeRank[];
  receiptsMap?: LeaderboardReceiptsMap; // NEW
  // Row-expansion breakdown context (2026-07-04): daily rows feed the journey
  // strip + Federer stats; cohort org/period feed CohortRecoveryCard (null in
  // company view — aggregated entries stamp the viewer's org, so the cohort
  // RPC would mis-scope for other-org reps).
  dailyRows: LeaderboardEntry[];
  timezone: string;
  cohortOrgId: string | null;
  cohortPeriodStart: string | null;
  dialsById?: Map<string, number>;
}> = ({
  sector,
  entries,
  userId,
  isUserSector,
  getSamples,
  prestigeRanks,
  receiptsMap,
  dailyRows,
  timezone,
  cohortOrgId,
  cohortPeriodStart,
  dialsById,
}) => {
  // Click-to-expand "why is this rep ranked here" panel — one open at a time.
  const [expandedRepId, setExpandedRepId] = React.useState<string | null>(null);
  // Stage B (Session 25): bar tracks points (Option B) so it agrees with rank.
  // P0-LBTOTAL: prestige_total when present; Math.max guards all-negative sectors.
  const maxScore = Math.max(entries[0]?.prestige_total ?? entries[0]?.points_earned ?? 0, 1);

  const [openReceipts, setOpenReceipts] = React.useState<{
    repId: string;
    metric: LeaderboardMetric;
    repName: string;
    metricLabel: string;
    formula: string;
  } | null>(null);

  // Compute per-sector normalization denominators once for tooltip computations.
  // Mirror computeWeightedRanking logic exactly: only qualified reps set the maxes.
  const gate = MIN_VOLUME_GATE[sector];
  const qualified = entries.filter(
    (e) => Number(e[gate.field as keyof LeaderboardEntry] ?? 0) >= gate.min
  );
  const safeMax = (vals: number[]) => Math.max(...vals, 0.001);
  const maxCC = safeMax(qualified.map((e) => e.cc_total || 0));
  const maxSR = safeMax(qualified.map((e) => e.show_rate || 0));
  const maxCB = safeMax(qualified.map((e) => e.calls_booked || 0));
  const maxCPC = safeMax(
    qualified.map((e) => {
      if (sector === "setter") return e.calls_booked > 0 ? e.cc_total / e.calls_booked : 0;
      return e.shows > 0 ? e.cc_total / e.shows : 0;
    })
  );

  return (
    <div className={isUserSector ? "ring-1 ring-blue-400/20" : ""} style={{ borderRadius: 20 }}>
      {/* Podium for top 3 */}
      {entries.length >= 2 && (
        <Podium
          entries={entries}
          sector={sector}
          prestigeRanks={prestigeRanks}
          dialsById={dialsById}
        />
      )}

      {/* Empty state — slim bar */}
      {entries.length === 0 ? (
        <div className="px-6 py-4 text-sm text-glass-muted">
          No {SECTOR_LABELS[sector].toLowerCase()} data for this period.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/[0.06] text-left token-10">
                <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase w-16">
                  <MetricInfoIcon metricKey={`lb.${sector}Rank`} getSamples={getSamples}>
                    Rank
                  </MetricInfoIcon>
                </th>
                <th
                  className="px-6 py-2.5 font-semibold text-glass-muted uppercase sticky left-0 z-10"
                  style={{ background: "var(--glass-background)" }}
                >
                  Name
                </th>
                {sector === "closer" && (
                  <>
                    <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                      <MetricInfoIcon metricKey="lb.cashCollected" getSamples={getSamples}>
                        Cash
                      </MetricInfoIcon>
                    </th>
                    <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                      <MetricInfoIcon metricKey="lb.showRate" getSamples={getSamples}>
                        Show Rate
                      </MetricInfoIcon>
                    </th>
                    <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                      <MetricInfoIcon metricKey="lb.closerCPC" getSamples={getSamples}>
                        CPC
                      </MetricInfoIcon>
                    </th>
                  </>
                )}
                {sector === "setter" && (
                  <>
                    <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                      <MetricInfoIcon metricKey="lb.setterCPC" getSamples={getSamples}>
                        CPC
                      </MetricInfoIcon>
                    </th>
                    <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                      <MetricInfoIcon metricKey="lb.showRate" getSamples={getSamples}>
                        Show Rate
                      </MetricInfoIcon>
                    </th>
                    <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                      <MetricInfoIcon metricKey="lb.callsBooked" getSamples={getSamples}>
                        Booked
                      </MetricInfoIcon>
                    </th>
                  </>
                )}
                {sector === "dialer" && (
                  // Dialer mirrors the setter columns (2026-06-02, Session 25).
                  // Keys copied verbatim from the setter <th> block above.
                  <>
                    <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                      <MetricInfoIcon metricKey="lb.setterCPC" getSamples={getSamples}>
                        CPC
                      </MetricInfoIcon>
                    </th>
                    <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                      <MetricInfoIcon metricKey="lb.showRate" getSamples={getSamples}>
                        Show Rate
                      </MetricInfoIcon>
                    </th>
                    <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                      <MetricInfoIcon metricKey="lb.callsBooked" getSamples={getSamples}>
                        Booked
                      </MetricInfoIcon>
                    </th>
                  </>
                )}
                <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right w-[120px]">
                  Score
                </th>
                <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                  <MetricInfoIcon metricKey="lb.pointsEarned" getSamples={getSamples}>
                    Points
                  </MetricInfoIcon>
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, idx) => {
                const isCurrentUser = entry.rep_id === userId;
                const rank = entry.rank;
                const isTop3 = rank >= 1 && rank <= 3;
                const isUnranked = rank === 0 || rank === null;

                // Progressive row styling
                const rowHeight = rank === 1 ? 56 : isTop3 ? 48 : 40;
                // Token classes for progressive name hierarchy
                const nameTokenClass =
                  rank === 1
                    ? "token-15" // T4: 15px/600
                    : isTop3
                      ? "token-14 font-semibold" // T8: 14px + semibold
                      : "token-13"; // T8.5: 13px/400

                const tierColor =
                  rank === 1
                    ? RANK_COLORS.gold
                    : rank === 2
                      ? RANK_COLORS.silver
                      : rank === 3
                        ? RANK_COLORS.bronze
                        : null;

                // Alternating row shading for #4+
                const zebraBg = !isTop3 && rank % 2 === 0 ? "rgba(0,0,0,0.025)" : undefined;

                const rowBg = isCurrentUser
                  ? "rgba(0,0,0,0.04)"
                  : tierColor
                    ? tierColor.bg
                    : zebraBg;

                const prestige = prestigeRanks.find((r) => r.rep_id === entry.rep_id);

                // --- Per-entry tooltip helpers ---
                const entryCPC = sectorCPC(entry, sector);
                const orphans = (entry as { orphan_precalls?: number }).orphan_precalls ?? 0;
                const srDenom = (entry.shows || 0) + (entry.no_shows || 0) + orphans;
                const srRaw = srDenom > 0 ? ((entry.shows || 0) / srDenom) * 100 : 0;
                const cpcDenom =
                  sector === "setter" || sector === "dialer"
                    ? entry.calls_booked || 0
                    : entry.shows || 0;
                const cpcDenomLabel =
                  sector === "setter" || sector === "dialer" ? "booked" : "shows";

                // Normalized values for score breakdown tooltip
                const ccN = (entry.cc_total || 0) / maxCC;
                const srN = (entry.show_rate || 0) / maxSR;
                const cpcN = entryCPC / maxCPC;
                const cbN = (entry.calls_booked || 0) / maxCB;

                // Score breakdown computation string
                let scoreComputation: string;
                if (isUnranked) {
                  scoreComputation = `unranked: ${gate.field} ${Number(entry[gate.field as keyof LeaderboardEntry] ?? 0)} < ${gate.min} required`;
                } else if (sector === "closer") {
                  scoreComputation = `${ccN.toFixed(2)}×50% + ${srN.toFixed(2)}×25% + ${cpcN.toFixed(2)}×25% = ${((entry.rank_score || 0) * 1).toFixed(3)}`;
                } else {
                  // setter + dialer share one formula (dialer mirrored 2026-06-02,
                  // Session 25): CPC 40 / Show Rate 30 / Booked 30.
                  scoreComputation = `${cpcN.toFixed(2)}×40% + ${srN.toFixed(2)}×30% + ${cbN.toFixed(2)}×30% = ${((entry.rank_score || 0) * 1).toFixed(3)}`;
                }

                const isExpanded = expandedRepId === entry.rep_id;

                return (
                  <React.Fragment key={entry.rep_id}>
                    <tr
                      className="border-b border-black/[0.04] hover:bg-black/[0.03] transition-colors cursor-pointer anim-pop"
                      style={{
                        height: rowHeight,
                        backgroundColor: rowBg,
                        animationDelay: `${Math.min(idx, 15) * 100}ms`,
                        animationDuration: "480ms",
                      }}
                      onClick={() => setExpandedRepId(isExpanded ? null : entry.rep_id)}
                      aria-expanded={isExpanded}
                      data-testid={`lb-row-${entry.rep_id}`}
                    >
                      {/* Rank + subtle left accent for top 3 */}
                      <td
                        className="px-6 py-2"
                        style={{
                          borderLeft: tierColor
                            ? `2px solid ${tierColor.accent}`
                            : "2px solid transparent",
                        }}
                      >
                        <RankBadge rank={rank} />
                      </td>

                      {/* Name with prestige badge — sticky identity column so it stays visible while
                          scrolling to sector metrics on narrow viewports (overflow-x-auto wrapper above).
                          NOTE: bg-[var(--glass-background)] Tailwind class compiles to background-color,
                          which silently no-ops on the gradient value — must use inline `background` shorthand. */}
                      <td
                        className="px-6 py-2 sticky left-0 z-10"
                        style={{ background: rowBg ?? "var(--glass-background)" }}
                      >
                        <div className="flex items-center gap-2">
                          {prestige && prestige.current_level >= 0 && (
                            <PrestigeBadge
                              level={prestige.current_level}
                              highestAchieved={prestige.highest_level_achieved}
                              size="sm"
                              showStars={false}
                            />
                          )}
                          <span className={`text-glass ${nameTokenClass}`}>
                            {entry.rep_name}
                            {isCurrentUser && (
                              <span className="ml-2 token-11 text-glass-muted font-normal">
                                (You)
                              </span>
                            )}
                          </span>
                          {/* Expand affordance — row click opens the breakdown panel */}
                          <svg
                            className={`w-3.5 h-3.5 text-glass-muted transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </td>

                      {/* Sector-specific columns — all numbers in mono, wrapped with ReceiptsPopover */}
                      {sector === "closer" && (
                        <>
                          <td
                            className="px-6 py-2 text-right token-13m text-glass"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ReceiptsPopover
                              repId={entry.rep_id}
                              metric="cash"
                              label="Cash"
                              formula="Cash = SUM(transactions.amount) for cash-collected events"
                              computation={
                                isUnranked
                                  ? `unranked: shows ${entry.shows || 0} < ${gate.min} required`
                                  : `${formatUSD(entry.cc_total)} ÷ ${formatUSD(maxCC)} (best) = ${ccN.toFixed(2)} norm`
                              }
                              receipts={receiptsMap?.[entry.rep_id]?.cash}
                              onOpenModal={() =>
                                setOpenReceipts({
                                  repId: entry.rep_id,
                                  metric: "cash",
                                  repName: entry.rep_name,
                                  metricLabel: "Cash Collected",
                                  formula:
                                    "Cash = SUM(transactions.amount) for cash-collected events",
                                })
                              }
                            >
                              <span>{formatUSD(entry.cc_total)}</span>
                            </ReceiptsPopover>
                          </td>
                          <td
                            className="px-6 py-2 text-right token-13m text-glass-secondary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ReceiptsPopover
                              repId={entry.rep_id}
                              metric="show_rate"
                              label="Show Rate"
                              formula="Show Rate = shows / (shows + no_shows + orphan_precalls)"
                              computation={
                                isUnranked
                                  ? `unranked: shows ${entry.shows || 0} < ${gate.min} required`
                                  : `${entry.shows || 0} / (${entry.shows || 0}+${entry.no_shows || 0}+${orphans}) = ${srRaw.toFixed(1)}%`
                              }
                              receipts={receiptsMap?.[entry.rep_id]?.show_rate}
                              onOpenModal={() =>
                                setOpenReceipts({
                                  repId: entry.rep_id,
                                  metric: "show_rate",
                                  repName: entry.rep_name,
                                  metricLabel: "Show Rate",
                                  formula:
                                    "Show Rate = shows / (shows + no_shows + orphan_precalls)",
                                })
                              }
                            >
                              <span>{formatPercent((entry.show_rate ?? 0) * 100)}</span>
                            </ReceiptsPopover>
                          </td>
                          <td
                            className="px-6 py-2 text-right token-13m text-glass-secondary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ReceiptsPopover
                              repId={entry.rep_id}
                              metric="cpc"
                              label="CPC"
                              formula={`CPC = cc_total / ${cpcDenomLabel}`}
                              computation={
                                isUnranked
                                  ? `unranked: shows ${entry.shows || 0} < ${gate.min} required`
                                  : `${formatUSD(entry.cc_total)} / ${cpcDenom} = ${formatUSD(entryCPC)} — norm vs best ${formatUSD(maxCPC)}`
                              }
                              receipts={receiptsMap?.[entry.rep_id]?.cpc}
                              onOpenModal={() =>
                                setOpenReceipts({
                                  repId: entry.rep_id,
                                  metric: "cpc",
                                  repName: entry.rep_name,
                                  metricLabel: "Cash Per Call",
                                  formula: `CPC = cc_total / ${cpcDenomLabel}`,
                                })
                              }
                            >
                              <span>{formatUSD(entry.cpc ?? 0)}</span>
                            </ReceiptsPopover>
                          </td>
                        </>
                      )}
                      {sector === "setter" && (
                        <>
                          <td
                            className="px-6 py-2 text-right token-13m text-glass"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ReceiptsPopover
                              repId={entry.rep_id}
                              metric="cpc"
                              label="CPC"
                              formula={`CPC = cc_total / ${cpcDenomLabel}`}
                              computation={
                                isUnranked
                                  ? `unranked: calls_booked ${entry.calls_booked || 0} < ${gate.min} required`
                                  : `${formatUSD(entry.cc_total)} / ${cpcDenom} = ${formatUSD(entryCPC)} — norm vs best ${formatUSD(maxCPC)}`
                              }
                              receipts={receiptsMap?.[entry.rep_id]?.cpc}
                              onOpenModal={() =>
                                setOpenReceipts({
                                  repId: entry.rep_id,
                                  metric: "cpc",
                                  repName: entry.rep_name,
                                  metricLabel: "Cash Per Call",
                                  formula: `CPC = cc_total / ${cpcDenomLabel}`,
                                })
                              }
                            >
                              <span>{formatUSD(entry.cpc ?? 0)}</span>
                            </ReceiptsPopover>
                          </td>
                          <td
                            className="px-6 py-2 text-right token-13m text-glass-secondary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ReceiptsPopover
                              repId={entry.rep_id}
                              metric="show_rate"
                              label="Show Rate"
                              formula="Show Rate = shows / (shows + no_shows + orphan_precalls)"
                              computation={
                                isUnranked
                                  ? `unranked: calls_booked ${entry.calls_booked || 0} < ${gate.min} required`
                                  : `${entry.shows || 0} / (${entry.shows || 0}+${entry.no_shows || 0}+${orphans}) = ${srRaw.toFixed(1)}%`
                              }
                              receipts={receiptsMap?.[entry.rep_id]?.show_rate}
                              onOpenModal={() =>
                                setOpenReceipts({
                                  repId: entry.rep_id,
                                  metric: "show_rate",
                                  repName: entry.rep_name,
                                  metricLabel: "Show Rate",
                                  formula:
                                    "Show Rate = shows / (shows + no_shows + orphan_precalls)",
                                })
                              }
                            >
                              <span>{formatPercent((entry.show_rate ?? 0) * 100)}</span>
                            </ReceiptsPopover>
                          </td>
                          <td
                            className="px-6 py-2 text-right token-13m text-glass-secondary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ReceiptsPopover
                              repId={entry.rep_id}
                              metric="calls_booked"
                              label="Booked"
                              formula="Booked = COUNT(sales_pre_call) for the rep × range"
                              computation={
                                isUnranked
                                  ? `unranked: calls_booked ${entry.calls_booked || 0} < ${gate.min} required`
                                  : `${entry.calls_booked || 0} ÷ ${maxCB} (best) = ${cbN.toFixed(2)} norm`
                              }
                              receipts={receiptsMap?.[entry.rep_id]?.calls_booked}
                              onOpenModal={() =>
                                setOpenReceipts({
                                  repId: entry.rep_id,
                                  metric: "calls_booked",
                                  repName: entry.rep_name,
                                  metricLabel: "Calls Booked",
                                  formula: "Booked = COUNT(sales_pre_call) for the rep × range",
                                })
                              }
                            >
                              <span>
                                {entry.calls_booked > 0
                                  ? entry.calls_booked
                                  : (entry.shows || 0) + (entry.no_shows || 0)}
                              </span>
                            </ReceiptsPopover>
                          </td>
                        </>
                      )}
                      {sector === "dialer" && (
                        // Dialer mirrors the setter cells (2026-06-02, Session 25):
                        // CPC / Show Rate / Booked. Dials + CTB columns removed —
                        // daily_dials + connect_to_book_rate are never populated.
                        <>
                          <td
                            className="px-6 py-2 text-right token-13m text-glass"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ReceiptsPopover
                              repId={entry.rep_id}
                              metric="cpc"
                              label="CPC"
                              formula={`CPC = cc_total / ${cpcDenomLabel}`}
                              computation={
                                isUnranked
                                  ? `unranked: calls_booked ${entry.calls_booked || 0} < ${gate.min} required`
                                  : `${formatUSD(entry.cc_total)} / ${cpcDenom} = ${formatUSD(entryCPC)} — norm vs best ${formatUSD(maxCPC)}`
                              }
                              receipts={receiptsMap?.[entry.rep_id]?.cpc}
                              onOpenModal={() =>
                                setOpenReceipts({
                                  repId: entry.rep_id,
                                  metric: "cpc",
                                  repName: entry.rep_name,
                                  metricLabel: "Cash Per Call",
                                  formula: `CPC = cc_total / ${cpcDenomLabel}`,
                                })
                              }
                            >
                              <span>{formatUSD(entry.cpc ?? 0)}</span>
                            </ReceiptsPopover>
                          </td>
                          <td
                            className="px-6 py-2 text-right token-13m text-glass-secondary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ReceiptsPopover
                              repId={entry.rep_id}
                              metric="show_rate"
                              label="Show Rate"
                              formula="Show Rate = shows / (shows + no_shows + orphan_precalls)"
                              computation={
                                isUnranked
                                  ? `unranked: calls_booked ${entry.calls_booked || 0} < ${gate.min} required`
                                  : `${entry.shows || 0} / (${entry.shows || 0}+${entry.no_shows || 0}+${orphans}) = ${srRaw.toFixed(1)}%`
                              }
                              receipts={receiptsMap?.[entry.rep_id]?.show_rate}
                              onOpenModal={() =>
                                setOpenReceipts({
                                  repId: entry.rep_id,
                                  metric: "show_rate",
                                  repName: entry.rep_name,
                                  metricLabel: "Show Rate",
                                  formula:
                                    "Show Rate = shows / (shows + no_shows + orphan_precalls)",
                                })
                              }
                            >
                              <span>{formatPercent((entry.show_rate ?? 0) * 100)}</span>
                            </ReceiptsPopover>
                          </td>
                          <td
                            className="px-6 py-2 text-right token-13m text-glass-secondary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ReceiptsPopover
                              repId={entry.rep_id}
                              metric="calls_booked"
                              label="Booked"
                              formula="Booked = COUNT(sales_pre_call) for the rep × range (solo bookings)"
                              computation={
                                isUnranked
                                  ? `unranked: calls_booked ${entry.calls_booked || 0} < ${gate.min} required`
                                  : `${entry.calls_booked || 0} ÷ ${maxCB} (best) = ${cbN.toFixed(2)} norm`
                              }
                              receipts={receiptsMap?.[entry.rep_id]?.calls_booked}
                              onOpenModal={() =>
                                setOpenReceipts({
                                  repId: entry.rep_id,
                                  metric: "calls_booked",
                                  repName: entry.rep_name,
                                  metricLabel: "Calls Booked",
                                  formula:
                                    "Booked = COUNT(sales_pre_call) for the rep × range (solo bookings)",
                                })
                              }
                            >
                              <span>
                                {entry.calls_booked > 0
                                  ? entry.calls_booked
                                  : (entry.shows || 0) + (entry.no_shows || 0)}
                              </span>
                            </ReceiptsPopover>
                          </td>
                        </>
                      )}

                      {/* Score bar — wrapped with ReceiptsPopover showing weighted blend */}
                      <td className="px-6 py-2" onClick={(e) => e.stopPropagation()}>
                        <ReceiptsPopover
                          repId={entry.rep_id}
                          metric="score"
                          label="Score"
                          formula={
                            sector === "closer"
                              ? "rank_score = 50%×Cash_norm + 25%×ShowRate_norm + 25%×CPC_norm"
                              : sector === "setter"
                                ? "rank_score = 40%×CPC_norm + 30%×ShowRate_norm + 30%×Booked_norm"
                                : "rank_score = 30%×CPC_norm + 25%×Dials_norm + 25%×CTB_norm + 20%×ShowRate_norm"
                          }
                          computation={scoreComputation}
                          receipts={receiptsMap?.[entry.rep_id]?.score}
                          onOpenModal={() =>
                            setOpenReceipts({
                              repId: entry.rep_id,
                              metric: "score",
                              repName: entry.rep_name,
                              metricLabel: "Rank Score",
                              formula:
                                sector === "closer"
                                  ? "rank_score = 50%×Cash_norm + 25%×ShowRate_norm + 25%×CPC_norm"
                                  : sector === "setter"
                                    ? "rank_score = 40%×CPC_norm + 30%×ShowRate_norm + 30%×Booked_norm"
                                    : "rank_score = 30%×CPC_norm + 25%×Dials_norm + 25%×CTB_norm + 20%×ShowRate_norm",
                            })
                          }
                        >
                          <ScoreBar
                            score={entry.prestige_total ?? entry.points_earned ?? 0}
                            maxScore={maxScore}
                            rank={rank}
                          />
                        </ReceiptsPopover>
                      </td>

                      {/* Points — wrapped with ReceiptsPopover */}
                      <td className="px-6 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <ReceiptsPopover
                          repId={entry.rep_id}
                          metric="points"
                          label="Points"
                          formula="1st=5, 2nd=3, 3rd=2, 4th=1, 5th+=0 (sum across days)"
                          computation={
                            // P0-LBTOTAL breakdown (decision 6 locked format, ASCII signs):
                            // "{lb} lb {+|-} {|kpi|} kpi + {mvp} mvp = {total} pts"
                            entry.prestige_total != null
                              ? `${entry.points_earned || 0} lb ${(entry.kpi_points ?? 0) < 0 ? "-" : "+"} ${Math.abs(entry.kpi_points ?? 0)} kpi + ${entry.mvp_points ?? 0} mvp = ${entry.prestige_total} pts`
                              : `${entry.points_earned || 0} pts earned this period`
                          }
                          receipts={receiptsMap?.[entry.rep_id]?.points}
                          onOpenModal={() =>
                            setOpenReceipts({
                              repId: entry.rep_id,
                              metric: "points",
                              repName: entry.rep_name,
                              metricLabel: "Rank Points",
                              formula: "1st=5, 2nd=3, 3rd=2, 4th=1, 5th+=0 (sum across days)",
                            })
                          }
                        >
                          <PointBadge
                            rank={rank}
                            points={entry.prestige_total ?? entry.points_earned}
                          />
                        </ReceiptsPopover>
                      </td>
                    </tr>
                    {/* Inline breakdown — the drawer's SCORE BREAKDOWN visuals, on the page */}
                    {isExpanded && (
                      <tr className="border-b border-black/[0.04]">
                        <td colSpan={7} className="p-0">
                          <RepBreakdownPanel
                            entry={entry}
                            sector={sector}
                            cohortEntries={entries}
                            dailyRows={dailyRows}
                            timezone={timezone}
                            cohortOrgId={cohortOrgId}
                            cohortPeriodStart={cohortPeriodStart}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {openReceipts && (
        <ReceiptsModal
          open={!!openReceipts}
          onClose={() => setOpenReceipts(null)}
          repName={openReceipts.repName}
          metric={openReceipts.metric}
          metricLabel={openReceipts.metricLabel}
          formula={openReceipts.formula}
          receipts={receiptsMap?.[openReceipts.repId]?.[openReceipts.metric]}
        />
      )}
    </div>
  );
};

// --- Loading Skeleton ---
const LeaderboardSkeleton: React.FC = () => (
  <div className="space-y-5 animate-fade-in-up pb-20">
    <div className="liquid-glass p-6 rounded-[20px]">
      <div className="h-6 bg-black/10 rounded w-48 anim-skeleton" />
      <div className="h-4 bg-black/5 rounded w-32 mt-2 anim-skeleton" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-5">
      {[1, 2, 3].map((i) => (
        <div key={i} className="liquid-glass p-6 rounded-[20px]">
          <div className="h-3 bg-black/10 rounded w-20 anim-skeleton" />
          <div className="h-10 bg-black/5 rounded w-24 mt-2 anim-skeleton" />
        </div>
      ))}
    </div>
    <div className="liquid-glass p-6 rounded-[20px]">
      <div className="h-5 bg-black/10 rounded w-40 mb-4 anim-skeleton" />
      <div className="space-y-3">
        {[1, 2, 3, 4].map((j) => (
          <div key={j} className="h-10 bg-black/5 rounded anim-skeleton" />
        ))}
      </div>
    </div>
  </div>
);

// --- Main Page ---
const REP_ROLES = ["closer", "setter", "dialer"];

const LeaderboardPage: React.FC = () => {
  const { user } = useAuth();
  const {
    currentOrganization,
    availableOrganizations,
    prestigePoints,
    prestigeRanks,
    teamMembers,
  } = useData();
  const { canViewAllPods, hasTunnelVision, canManageTeam, isRealExec, isManager, managedPodIds } =
    useRBAC();
  const orgTimezone = currentOrganization?.timezone || "UTC";

  const [viewTab, setViewTab] = usePersistedState<ViewTab>("altara_lb_viewTab", "pod");
  const { dateRange, setDateRange, navigateDateRange } = useGlobalDateRange();

  // Rep selector for execs/managers — view hero stats as a specific rep
  const LB_REP_KEY = "altara_lb_selected_rep";
  const [selectedRepId, setSelectedRepId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LB_REP_KEY) || null;
    } catch {
      return null;
    }
  });
  const handleRepChange = (repId: string | null) => {
    setSelectedRepId(repId);
    try {
      if (repId) localStorage.setItem(LB_REP_KEY, repId);
      else localStorage.removeItem(LB_REP_KEY);
    } catch {
      /* localStorage unavailable */
    }
  };
  const repList = useMemo(() => {
    if (hasTunnelVision) return [];
    return teamMembers
      .filter((m) => REP_ROLES.includes(m.role) && m.status === "active")
      .sort((a, b) => a.user_name.localeCompare(b.user_name));
  }, [teamMembers, hasTunnelVision]);

  // Effective user for hero banner — selected rep or self
  const effectiveUserId = (!hasTunnelVision && selectedRepId) || user?.id;
  const selectedRep = useMemo(
    () => repList.find((r) => r.user_id === selectedRepId),
    [repList, selectedRepId]
  );
  const isViewingOther = !hasTunnelVision && selectedRepId && selectedRepId !== user?.id;

  // Sector tab state — use effective user's sector if viewing as someone else
  const effectiveSector = isViewingOther
    ? (selectedRep?.role as SectorFilter | undefined)
    : (user?.sector as SectorFilter | undefined);
  const userSector = effectiveSector;
  const [activeSector, setActiveSector] = usePersistedState<SectorFilter>(
    "altara_lb_sector",
    userSector || "closer"
  );
  // SA6 (2026-10-06): real dial counts for the dialer podium, fetched only on the dialer tab.
  const { data: dialerCards } = useDialerScorecards(
    activeSector === "dialer" ? (currentOrganization?.id ?? null) : null,
    dateRange
  );
  const dialsById = useMemo(
    () => new Map(dialerCards.map((d) => [d.dialerId, d.dials])),
    [dialerCards]
  );
  const [slideDirection, setSlideDirection] = useState<"left" | "right" | null>(null);
  const sectorContentRef = useRef<HTMLDivElement>(null);

  const allOrgIds = useMemo(
    () => availableOrganizations.map((o) => o.id),
    [availableOrganizations]
  );

  const isLiveMode = useMemo(() => {
    if (!dateRange.start || !dateRange.end || dateRange.isAllTime) return false;
    const today = timestampToOrgDateKey(new Date().toISOString(), orgTimezone);
    const startKey = timestampToOrgDateKey(dateRange.start.toISOString(), orgTimezone);
    const endKey = timestampToOrgDateKey(dateRange.end.toISOString(), orgTimezone);
    return startKey === today && endKey === today;
    // NOTE: today is computed once per dep change, not on a tick. After midnight (org tz) the
    // user must re-pick "today" preset for live mode to reactivate.
  }, [dateRange.start, dateRange.end, dateRange.isAllTime, orgTimezone]);

  // Memoize so useLeaderboard's downstream useMemo/useCallback deps stay stable.
  // The previous inline object created a new {startDate, endDate} every render,
  // and `endDate: dateRange.end || new Date()` minted a fresh Date each render
  // when end was null — both fed dep churn into the realtime subscription path.
  const leaderboardFilters = useMemo(
    () =>
      isLiveMode
        ? undefined
        : {
            startDate: dateRange.start as Date,
            endDate: dateRange.end || new Date(),
            viewMode: "aggregate" as const,
          },
    [isLiveMode, dateRange.start, dateRange.end]
  );

  const {
    leaderboard,
    companyWideLeaderboard,
    kpiDeltas,
    isLoading: isLoadingData,
    isRealtimeConnected,
    refresh: refreshLeaderboard,
  } = useLeaderboard({
    orgId: currentOrganization?.id || null,
    allOrgIds,
    includeCompanyWide: true,
    filters: leaderboardFilters,
    enableRealtime: true,
  });

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCalculations, setShowCalculations] = useState(false);
  const [showMvpVoting, setShowMvpVoting] = useState(false);
  const [showMvpAdmin, setShowMvpAdmin] = useState(false);

  // MVP voting hook
  const {
    votingStatus: mvpStatus,
    teammates: mvpTeammates,
    myVote: mvpMyVote,
    castVote: mvpCastVote,
    isVotingVisible: mvpVisible,
  } = useMvpVoting(currentOrganization?.id || null);

  const mvpMonthLabel = mvpStatus
    ? new Date(mvpStatus.month + "T12:00:00Z").toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })
    : "";

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refreshLeaderboard();
    setIsRefreshing(false);
  }, [refreshLeaderboard]);

  const activeLeaderboard = viewTab === "company" ? companyWideLeaderboard : leaderboard;

  // Cohort-recovery context for the inline breakdown panels (2026-07-04).
  // Company view gets null — aggregated entries stamp the viewer's org onto
  // every row (P-2), so a per-rep cohort read would mis-scope for other-org
  // reps. Period = month bucket of the earliest date in range (mirrors the
  // CalculationsDrawer cohortContext derivation).
  const cohortPeriodStart = useMemo(() => {
    if (viewTab === "company") return null;
    const dates = activeLeaderboard
      .map((e) => e.date)
      .filter((d): d is string => Boolean(d))
      .sort();
    return dates.length ? dates[0].slice(0, 7) + "-01" : null;
  }, [viewTab, activeLeaderboard]);

  // Pod filter
  const allPods = useMemo(() => {
    const podMap = new Map<string, { id: string; name: string; color: string }>();
    activeLeaderboard.forEach((e) => {
      if (e.pod_id && !podMap.has(e.pod_id)) {
        podMap.set(e.pod_id, {
          id: e.pod_id,
          name: e.pod_name || "Unknown Pod",
          color: e.pod_color || HEX.accent,
        });
      }
    });
    return Array.from(podMap.values());
  }, [activeLeaderboard]);

  const [selectedPodId, setSelectedPodId] = useState<string | null>(null);
  // activePodId resolution:
  // - exec: uses selectedPodId (can pick any pod, null = all pods)
  // - manager: uses selectedPodId scoped to managedPodIds, defaults to first managed pod
  // - reps (tunnel vision): locked to own podId
  const activePodId: string | null = (() => {
    if (canViewAllPods) return selectedPodId;
    if (isManager && managedPodIds.length > 0) {
      // If manager selected a pod that they manage, honor it; else default to first
      return selectedPodId && managedPodIds.includes(selectedPodId)
        ? selectedPodId
        : managedPodIds[0];
    }
    return user?.podId || null;
  })();
  const activePodName = allPods.find((p) => p.id === activePodId)?.name;

  const orgIdsForReceipts = React.useMemo<string[]>(() => {
    if (viewTab === "company") return allOrgIds;
    return currentOrganization?.id ? [currentOrganization.id] : [];
  }, [viewTab, allOrgIds, currentOrganization]);

  const startKeyForReceipts = dateRange.start ? toOrgDateKey(dateRange.start) : "";
  const endKeyForReceipts = dateRange.end ? toOrgDateKey(dateRange.end) : "";

  const { receipts: receiptsMap } = useLeaderboardReceipts({
    orgIds: orgIdsForReceipts,
    startDate: startKeyForReceipts,
    endDate: endKeyForReceipts,
    sector: activeSector,
    podId: viewTab === "pod" ? (activePodId ?? undefined) : undefined,
    enabled: orgIdsForReceipts.length > 0 && !!startKeyForReceipts && !!endKeyForReceipts,
  });

  // Pods visible in the pod picker — exec sees all, manager sees only their pods
  const selectablePods = canViewAllPods
    ? allPods
    : isManager
      ? allPods.filter((p) => managedPodIds.includes(p.id))
      : [];

  const latestDate = useMemo(() => {
    if (!activeLeaderboard.length) return null;
    return activeLeaderboard[0]?.date || null;
  }, [activeLeaderboard]);

  // Compute entries for a given sector
  const computeSectorEntries = useCallback(
    (sector: SectorFilter) => {
      let filtered = activeLeaderboard.filter((e) => e.sector === sector);

      if (viewTab === "pod" && activePodId) {
        filtered = filtered.filter((e) => e.pod_id === activePodId);
      }

      if (isLiveMode) {
        if (!latestDate) return [];
        let dayEntries = filtered
          .filter((e) => e.date === latestDate)
          .filter((e) => e.rank != null)
          .map((e) => ({
            ...e,
            show_rate: (e.show_rate ?? 0) / 100,
            close_rate: (e.close_rate ?? 0) / 100,
            connect_to_book_rate: (e.connect_to_book_rate ?? 0) / 100,
            cpc: sectorCPC(e, sector),
          }));

        if (viewTab === "company") {
          // Stage B (Session 25): points-first, rank_score tiebreak (matches range mode).
          dayEntries.sort(
            (a, b) =>
              (b.points_earned || 0) - (a.points_earned || 0) ||
              (b.rank_score ?? 0) - (a.rank_score ?? 0)
          );
          dayEntries = dayEntries.map((e, i) => ({ ...e, rank: i + 1 }));
          return dayEntries.slice(0, COMPANY_WIDE_TOP_N);
        }

        dayEntries.sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
        return dayEntries;
      }

      // Range mode
      const startStr = dateRange.start ? toOrgDateKey(dateRange.start) : "";
      const endStr = dateRange.end ? toOrgDateKey(dateRange.end) : "9999-12-31";
      const inRange = filtered.filter((e) => e.date >= startStr && e.date <= endStr);

      interface AggregatedEntry {
        rep_id: string;
        rep_name: string;
        rep_email: string;
        sector: string;
        pod_id: string;
        pod_name: string;
        pod_color: string;
        cc_total: number;
        calls_taken: number;
        calls_booked: number;
        shows: number;
        no_shows: number;
        orphan_precalls: number;
        wins: number;
        pending_closes: number;
        losses: number;
        reschedules: number;
        daily_dials: number;
        points_earned: number;
        cpc: number;
        days: number;
      }

      const agg = new Map<string, AggregatedEntry>();
      inRange.forEach((e) => {
        const existing = agg.get(e.rep_id);
        if (existing) {
          existing.cc_total += e.cc_total || 0;
          existing.calls_taken += e.calls_taken || 0;
          existing.calls_booked += e.calls_booked || 0;
          existing.shows += e.shows || 0;
          existing.no_shows += e.no_shows || 0;
          existing.orphan_precalls += e.orphan_precalls || 0;
          existing.wins += e.wins || 0;
          existing.pending_closes += e.pending_closes || 0;
          existing.losses += e.losses || 0;
          existing.reschedules += e.reschedules || 0;
          existing.daily_dials += e.daily_dials || 0;
          existing.points_earned += e.points_earned || 0;
          existing.days += 1;
        } else {
          agg.set(e.rep_id, {
            rep_id: e.rep_id,
            rep_name: e.rep_name,
            rep_email: e.rep_email,
            sector: e.sector,
            pod_id: e.pod_id,
            pod_name: e.pod_name,
            pod_color: e.pod_color,
            cc_total: e.cc_total || 0,
            calls_taken: e.calls_taken || 0,
            calls_booked: e.calls_booked || 0,
            shows: e.shows || 0,
            no_shows: e.no_shows || 0,
            orphan_precalls: e.orphan_precalls || 0,
            wins: e.wins || 0,
            pending_closes: e.pending_closes || 0,
            losses: e.losses || 0,
            reschedules: e.reschedules || 0,
            daily_dials: e.daily_dials || 0,
            cpc: e.cpc || 0,
            points_earned: e.points_earned || 0,
            days: 1,
          });
        }
      });

      // P0-LBTOTAL (2026-07-03): prestige-total components. MVP is a month-end
      // award — only calendar months FULLY inside the range count.
      const covered = coveredMonths(startStr, endStr);
      const isCompanyView = viewTab === "company";

      const aggregated: LeaderboardEntry[] = Array.from(agg.values()).map((entry) => {
        // kpiDeltas is keyed by rep_id ALONE (see useLeaderboard) — this map
        // stamps currentOrganization onto every aggregated entry below, so an
        // org-composite key would silently miss other-org reps in company view.
        const kpi_points = kpiDeltas.get(entry.rep_id) ?? 0;
        // Company view: FORCED 0 — prestigePoints is org-scoped via DataContext,
        // so letting the lookup run would add MVP for same-org reps only →
        // inconsistent totals within one company table. No sector filter on the
        // org-view sum: MVP votes are person-scoped (REF:350).
        const mvp_points = isCompanyView
          ? 0
          : prestigePoints
              .filter((p) => p.rep_id === entry.rep_id && covered.includes(p.month ?? ""))
              .reduce((sum, p) => sum + (p.mvp_points || 0), 0);
        const prestige_total = (entry.points_earned || 0) + kpi_points + mvp_points;
        return {
          ...entry,
          date: "",
          organization_id: currentOrganization?.id || "",
          organization_name: currentOrganization?.name || "",
          // P0f-followup: include orphan_precalls in denominator per /metrics canonical Show Rate
          // (shows / (shows + no_shows + orphan_precalls)). Pre-fix omitted orphans, inflating
          // range-mode show rates for any sector.
          show_rate: (() => {
            const denom = entry.shows + entry.no_shows + (entry.orphan_precalls ?? 0);
            return denom > 0 ? entry.shows / denom : 0;
          })(),
          // FINAL close rate (R21/R24/R29/R30, Mako 2026-08-09). Denominator is decisiveCalls
          // = shows − reschedules, NOT shows: a reschedule is attendance without a decision, so
          // it counts for show rate and not for close rate. Numerator is wins only (PIF + CPP),
          // the half gates and prestige score on. Replaces the 2026-06-19 provisional doctrine,
          // which divided (wins + pending_closes) by shows.
          close_rate: (() => {
            const decisiveCalls = entry.shows - entry.reschedules;
            return decisiveCalls > 0 ? entry.wins / decisiveCalls : 0;
          })(),
          // R54 (2026-08-17): as-built calls_booked/daily_dials formula killed; R17
          // compute ships with the dial-analytics rebuild. Field dead — dialer view
          // dropped CTB 2026-06-02.
          connect_to_book_rate: 0,
          // Explicit copies — the aggregated-entry object is an allowlist
          // (/debug #78): a field not named here silently vanishes downstream.
          kpi_points,
          mvp_points,
          prestige_total,
          rank: 0,
          rank_score: null,
        };
      });

      const ranked = computeWeightedRanking(aggregated, sector);
      return viewTab === "company" ? ranked.slice(0, COMPANY_WIDE_TOP_N) : ranked;
    },
    [
      activeLeaderboard,
      viewTab,
      activePodId,
      dateRange,
      isLiveMode,
      latestDate,
      currentOrganization?.id,
      currentOrganization?.name,
      kpiDeltas,
      prestigePoints,
    ]
  );

  // Compute all 3 sector tables
  const closerEntries = useMemo(() => computeSectorEntries("closer"), [computeSectorEntries]);
  const setterEntries = useMemo(() => computeSectorEntries("setter"), [computeSectorEntries]);
  const dialerEntries = useMemo(() => computeSectorEntries("dialer"), [computeSectorEntries]);

  // P0.5 fix (May 14): hide the rep dropdown for managers who don't have their own
  // rep entry on the leaderboard. "Your Stats" would otherwise empty the table with a
  // misleading "No closers data" message for exec/admin/owner/client roles.
  const userId = user?.id;
  const hasOwnRepEntry = useMemo(() => {
    if (!userId) return false;
    return (
      closerEntries.some((e) => e.rep_id === userId) ||
      setterEntries.some((e) => e.rep_id === userId) ||
      dialerEntries.some((e) => e.rep_id === userId)
    );
  }, [userId, closerEntries, setterEntries, dialerEntries]);

  const sectorEntries = useMemo<Record<SectorFilter, LeaderboardEntry[]>>(
    () => ({
      closer: closerEntries,
      setter: setterEntries,
      dialer: dialerEntries,
    }),
    [closerEntries, setterEntries, dialerEntries]
  );

  // Available sectors (tunnel vision = only own sector)
  const availableSectors = useMemo((): SectorFilter[] => {
    if (hasTunnelVision && userSector) {
      return [userSector];
    }
    const all: SectorFilter[] = ["closer", "setter", "dialer"];
    // Float user's sector to front
    if (userSector && all.includes(userSector)) {
      return [userSector, ...all.filter((s) => s !== userSector)];
    }
    return all;
  }, [hasTunnelVision, userSector]);

  // Sector tab switching with animation direction
  const handleSectorChange = useCallback(
    (newSector: SectorFilter) => {
      if (newSector === activeSector) return;
      const sectors: SectorFilter[] = ["closer", "setter", "dialer"];
      const oldIdx = sectors.indexOf(activeSector);
      const newIdx = sectors.indexOf(newSector);
      setSlideDirection(newIdx > oldIdx ? "left" : "right");
      setActiveSector(newSector);
      // Reset animation after transition completes
      setTimeout(() => setSlideDirection(null), 350);
    },
    [activeSector, setActiveSector]
  );

  // Build header label per sector table
  const buildHeaderLabel = useCallback(
    (_sector: SectorFilter): string => {
      if (viewTab === "company") return `Company-Wide`;
      if (activePodName) return activePodName;
      if (!canViewAllPods && !activePodId) return "";
      return "All Pods";
    },
    [viewTab, activePodName, canViewAllPods, activePodId]
  );

  // Personal Stats — uses effectiveUserId so the hero banner reflects the selected rep
  const personalStats = useMemo(() => {
    const targetId = effectiveUserId;
    if (!targetId)
      return {
        rank: null,
        pointsToday: 0,
        monthlyPrestige: 0,
        prestigeRankInPod: null,
        daysLeft: 0,
        topScore: 0,
        myScore: 0,
      };

    const mySector = userSector || "closer";
    const myEntries = sectorEntries[mySector] || [];
    const myEntry = myEntries.find((e) => e.rep_id === targetId) || null;

    const now = new Date();
    const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const daysLeft = lastDay.getDate() - now.getDate();

    const prestigeEntry = prestigePoints.find(
      (p) =>
        p.rep_id === targetId && p.sector === mySector && (p.month ?? "").startsWith(monthPrefix)
    );
    const monthlyPoints =
      prestigeEntry?.total_points ??
      activeLeaderboard
        .filter(
          (e) => e.rep_id === targetId && e.sector === mySector && e.date.startsWith(monthPrefix)
        )
        .reduce((sum, e) => sum + (e.points_earned || 0), 0);

    return {
      rank: myEntry?.rank || null,
      // P0-LBTOTAL (diff-sweep finding 1): myEntry comes from the same
      // range-aggregated array as the table — the hero must show the same
      // total as the rep's own row. Live mode has no prestige_total →
      // falls back to today's lb points (decision 2 unchanged).
      pointsToday: myEntry?.prestige_total ?? myEntry?.points_earned ?? 0,
      monthlyPrestige: monthlyPoints,
      prestigeRankInPod: prestigeEntry?.rank_in_pod || null,
      daysLeft,
      topScore: myEntries[0]?.rank_score || 0,
      myScore: myEntry?.rank_score || 0,
    };
  }, [effectiveUserId, userSector, sectorEntries, activeLeaderboard, prestigePoints]);

  // Prestige standings for company-wide view
  const prestigeStandings = useMemo(() => {
    if (viewTab !== "company" || !prestigePoints.length) return [];
    const now = new Date();
    const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    let standings = prestigePoints.filter((p) => (p.month ?? "").startsWith(monthPrefix));

    if (hasTunnelVision && userSector) {
      standings = standings.filter((p) => p.sector === userSector);
    }

    return standings.sort((a, b) => (b.total_points || 0) - (a.total_points || 0));
  }, [viewTab, prestigePoints, hasTunnelVision, userSector]);

  // Metric tooltip samples
  const allSectorEntries = useMemo(
    () => [...closerEntries, ...setterEntries, ...dialerEntries],
    [closerEntries, setterEntries, dialerEntries]
  );

  const getLeaderboardSamples = useCallback(
    (metricKey: string): MetricSample[] => {
      const fmtPct = (v: number) => formatPercent(v * 100);
      const fmtDate = (d: string) => {
        if (!d) return "—";
        return formatDateKeyDisplay(d, orgTimezone);
      };
      const take5 = <T,>(arr: T[]) => arr.slice(0, 5);

      switch (metricKey) {
        case "lb.cashCollected":
          return take5(
            [...allSectorEntries].sort((a, b) => (b.cc_total || 0) - (a.cc_total || 0))
          ).map((e) => ({
            label: e.rep_name,
            value: formatUSD(e.cc_total || 0),
            date: fmtDate(e.date),
          }));
        case "lb.showRate":
          return take5(
            [...allSectorEntries].sort((a, b) => (b.show_rate || 0) - (a.show_rate || 0))
          ).map((e) => ({
            label: e.rep_name,
            value: fmtPct(e.show_rate || 0),
            date: fmtDate(e.date),
          }));
        case "lb.closerCPC":
        case "lb.setterCPC":
        case "lb.dialerCPC":
          return take5([...allSectorEntries].sort((a, b) => (b.cpc || 0) - (a.cpc || 0))).map(
            (e) => ({ label: e.rep_name, value: formatUSD(e.cpc || 0), date: fmtDate(e.date) })
          );
        case "lb.callsBooked":
          return take5(
            [...allSectorEntries].sort((a, b) => (b.calls_booked || 0) - (a.calls_booked || 0))
          ).map((e) => ({
            label: e.rep_name,
            value: `${e.calls_booked || 0}`,
            date: fmtDate(e.date),
          }));
        case "lb.dailyDials":
          return take5(
            [...allSectorEntries].sort((a, b) => (b.daily_dials || 0) - (a.daily_dials || 0))
          ).map((e) => ({
            label: e.rep_name,
            value: `${e.daily_dials || 0} dials`,
            date: fmtDate(e.date),
          }));
        case "lb.connectToBookRate":
          // R54 (2026-08-17): as-built calls_booked/daily_dials formula killed; R17
          // compute ships with the dial-analytics rebuild. Field dead — dialer view
          // dropped CTB 2026-06-02.
          return [];
        case "lb.pointsEarned":
          // P0-LBTOTAL: rank the sample by the same number the table ranks by.
          return take5(
            [...allSectorEntries].sort(
              (a, b) =>
                (b.prestige_total ?? b.points_earned ?? 0) -
                (a.prestige_total ?? a.points_earned ?? 0)
            )
          ).map((e) => ({
            label: e.rep_name,
            value: `${e.prestige_total ?? e.points_earned ?? 0} pts`,
            date: fmtDate(e.date),
          }));
        case "lb.closerRank":
        case "lb.setterRank":
        case "lb.dialerRank":
          return take5(
            [...allSectorEntries]
              .filter((e) => e.rank != null)
              .sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))
          ).map((e) => ({
            label: e.rep_name,
            value: `#${e.rank}${e.rank_score != null ? ` (${e.rank_score.toFixed(2)})` : ""}`,
            date: fmtDate(e.date),
          }));
        case "lb.monthlyPrestige":
          return take5(prestigeStandings).map((e) => ({
            label: e.rep_name,
            value: `${e.total_points || 0} pts`,
            date: new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" }),
          }));
        default:
          return [];
      }
    },
    [allSectorEntries, prestigeStandings, orgTimezone]
  );

  // Auto-switch to current month range when entering company-wide view
  const handleViewTabChange = useCallback(
    (tab: ViewTab) => {
      setViewTab(tab);
      if (tab === "company") {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        setDateRange({ start: monthStart, end: now });
      }
    },
    [setViewTab, setDateRange]
  );

  // Date label
  const dateLabel = useMemo(() => {
    if (isLiveMode) return "Today";
    if (dateRange.isAllTime) return "All Time";
    if (!dateRange.start) return "All Time";
    const startStr = dateRange.start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: orgTimezone,
    });
    const endStr = (dateRange.end || new Date()).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: orgTimezone,
    });
    return `${startStr} – ${endStr}`;
  }, [isLiveMode, dateRange.isAllTime, dateRange.start, dateRange.end, orgTimezone]);

  // User prestige info for hero banner
  const userPrestige = useMemo(() => {
    if (!effectiveUserId) return null;
    return prestigeRanks.find((r) => r.rep_id === effectiveUserId) || null;
  }, [effectiveUserId, prestigeRanks]);

  // Score gap percentage
  const scoreGapPct =
    personalStats.topScore > 0
      ? Math.round((personalStats.myScore / personalStats.topScore) * 100)
      : 0;

  // Loading state
  if (isLoadingData && !leaderboard.length) {
    return <LeaderboardSkeleton />;
  }

  if (!currentOrganization) {
    return <div className="p-8 text-sm text-glass-muted">Please select an organization.</div>;
  }

  // Block reps with no pod assignment. Managers are exempt if they have managedPodIds.
  if (
    !canViewAllPods &&
    !user?.podId &&
    !(isManager && managedPodIds.length > 0) &&
    viewTab === "pod"
  ) {
    return (
      <div className="space-y-5 animate-fade-in-up pb-20">
        <div className="liquid-glass anim-lift p-6 rounded-[20px]">
          <h1 className="token-24h text-glass">Leaderboard</h1>
          <p className="text-glass-secondary mt-1">{currentOrganization.name}</p>
        </div>
        <div className="liquid-glass anim-lift p-6 rounded-[20px]">
          <p className="text-sm text-glass-secondary">
            No pod assigned. Contact your manager to be assigned to a pod.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in-up pb-20">
      {/* Header — Row 1: title left, date range + exec icons right */}
      <div>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h1 className="token-20h text-glass">Leaderboard</h1>

          <DateRangePicker
            dateRange={dateRange}
            setDateRange={setDateRange}
            navigateDateRange={navigateDateRange}
          />
        </div>

        {/* Row 2: All pill controls — uniform sizing */}
        <div className="flex items-center gap-3 mt-3">
          {/* Pod / Company-Wide — Company-Wide is exec-only */}
          {canViewAllPods && (
            <div className="flex liquid-glass-light rounded-lg p-0.5">
              <button
                onClick={() => handleViewTabChange("pod")}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  viewTab === "pod"
                    ? "bg-black/[0.08] text-glass shadow-sm"
                    : "text-glass-secondary hover:text-glass"
                }`}
              >
                Pod
              </button>
              <button
                onClick={() => handleViewTabChange("company")}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  viewTab === "company"
                    ? "bg-black/[0.08] text-glass shadow-sm"
                    : "text-glass-secondary hover:text-glass"
                }`}
              >
                Company-Wide
              </button>
            </div>
          )}

          {/* Pod Selector — styled to match pill buttons */}
          {viewTab === "pod" && (canViewAllPods || (isManager && managedPodIds.length > 1)) && (
            <div className="relative inline-flex">
              <select
                value={selectedPodId || ""}
                onChange={(e) => setSelectedPodId(e.target.value || null)}
                className="liquid-glass-light border border-black/[0.06] text-glass text-xs font-semibold rounded-lg pl-3 pr-7 py-1.5 appearance-none cursor-pointer"
              >
                {canViewAllPods && <option value="">All Pods</option>}
                {selectablePods.map((pod) => (
                  <option key={pod.id} value={pod.id}>
                    {pod.name}
                  </option>
                ))}
              </select>
              <svg
                className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 pointer-events-none text-glass-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
              </svg>
            </div>
          )}

          {/* Rep Selector — styled to match pill buttons */}
          {/* P0.5: dropdown hidden for non-rep managers (no hasOwnRepEntry) — "Your Stats" was a footgun for them */}
          {canManageTeam && repList.length > 0 && hasOwnRepEntry && (
            <div className="relative inline-flex">
              <select
                value={selectedRepId || ""}
                onChange={(e) => handleRepChange(e.target.value || null)}
                className="liquid-glass-light border border-black/[0.06] text-glass text-xs font-semibold rounded-lg pl-3 pr-7 py-1.5 appearance-none cursor-pointer"
              >
                <option value="">Your Stats</option>
                {repList.map((rep) => (
                  <option key={rep.user_id} value={rep.user_id}>
                    {rep.user_name} ({rep.role})
                  </option>
                ))}
              </select>
              <svg
                className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 pointer-events-none text-glass-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Formula Guide — standing "How this is calculated" panel, sector-aware */}
      <FormulaGuide sector={activeSector} />

      {/* MVP Voting Banner */}
      {mvpVisible && mvpStatus && (
        <div className="flex items-center gap-3">
          {/* Vote CTA / status */}
          <button
            onClick={() => setShowMvpVoting(true)}
            className={`flex-1 flex items-center gap-3 px-4 py-3 rounded-[16px] transition-all ${
              mvpMyVote
                ? "liquid-glass-light border border-green-400/20"
                : "liquid-glass border border-ds-accent/30 hover:border-ds-accent/50"
            }`}
          >
            <span className="text-lg">{mvpMyVote ? "\u2713" : "\u2605"}</span>
            <div className="text-left">
              {mvpMyVote ? (
                <>
                  <p className="token-13 text-ds-green">
                    You voted for <strong>{mvpMyVote.nominee_name}</strong>
                  </p>
                  <p className="token-10 text-glass-muted">Tap to change your vote</p>
                </>
              ) : mvpStatus.voting_open ? (
                <>
                  <p className="token-13 text-glass">MVP Voting is Open</p>
                  <p className="token-10 text-glass-muted">Cast your vote for this month's MVP</p>
                </>
              ) : (
                <>
                  <p className="token-13 text-glass">MVP Voting</p>
                  <p className="token-10 text-glass-muted">
                    Opens in {mvpStatus.days_until_open} day
                    {mvpStatus.days_until_open !== 1 ? "s" : ""}
                  </p>
                </>
              )}
            </div>
            {mvpStatus.voting_open && !mvpMyVote && (
              <span className="ml-auto px-2 py-0.5 rounded-md bg-ds-accent-bg token-10u text-ds-accent">
                Vote Now
              </span>
            )}
          </button>

          {/* Admin tallies button — exec/manager only */}
          {canManageTeam && (
            <button
              onClick={() => setShowMvpAdmin(true)}
              className="px-3 py-3 rounded-[16px] liquid-glass-light hover:bg-black/[0.04] transition-colors"
              title="View vote tallies"
            >
              <svg
                className="w-5 h-5 text-glass-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Hero Banner — asymmetric 2fr/1fr/1fr (stacks below sm — labels don't wrap gracefully) */}
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-5">
        {/* YOUR RANK — dominant */}
        <Card className="rounded-[20px] anim-lift" variant="solid">
          <div className="flex items-center gap-5">
            {/* Rank number — hero size */}
            <div
              className="flex items-center justify-center rounded-2xl"
              style={{
                width: 80,
                height: 80,
                backgroundColor:
                  personalStats.rank === 1
                    ? RANK_COLORS.gold.bg
                    : personalStats.rank === 2
                      ? RANK_COLORS.silver.bg
                      : personalStats.rank === 3
                        ? RANK_COLORS.bronze.bg
                        : "rgba(0,0,0,0.04)",
                border:
                  personalStats.rank && personalStats.rank <= 3
                    ? `2px solid ${personalStats.rank === 1 ? RANK_COLORS.gold.accent : personalStats.rank === 2 ? RANK_COLORS.silver.accent : RANK_COLORS.bronze.accent}`
                    : "2px solid rgba(0,0,0,0.06)",
              }}
            >
              <span className="v2-hero-number text-glass">
                {personalStats.rank ? `${personalStats.rank}` : "–"}
              </span>
            </div>

            <div className="flex-1 min-w-0">
              <MetricInfoIcon
                metricKey={`lb.${userSector || "closer"}Rank`}
                getSamples={getLeaderboardSamples}
              >
                <p className="token-10 text-glass-muted uppercase">
                  {isViewingOther ? `${selectedRep?.user_name}'s Rank` : "Your Rank"}
                </p>
              </MetricInfoIcon>

              {/* Score gap bar */}
              {personalStats.rank && personalStats.rank > 1 && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-black/[0.06] overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${scoreGapPct}%`,
                          backgroundColor: RANK_COLORS.gold.accent,
                        }}
                      />
                    </div>
                    <span className="font-mono token-11 tabular-nums text-glass-muted">
                      {scoreGapPct}% of #1
                    </span>
                  </div>
                </div>
              )}

              <p className="text-glass-secondary text-xs mt-1.5 capitalize">
                {userSector || "—"} · {activePodName || currentOrganization?.name || "—"}
              </p>
            </div>
          </div>
        </Card>

        {/* POINTS TODAY — secondary */}
        <Card className="rounded-[20px] anim-lift" variant="solid">
          <MetricInfoIcon metricKey="lb.pointsEarned" getSamples={getLeaderboardSamples}>
            <p className="token-10 text-glass-muted uppercase">Points Today</p>
          </MetricInfoIcon>
          <p className="v2-secondary-number text-glass mt-1">{personalStats.pointsToday}</p>
        </Card>

        {/* MONTHLY PRESTIGE — secondary */}
        <Card className="rounded-[20px] anim-lift" variant="solid">
          <MetricInfoIcon metricKey="lb.monthlyPrestige" getSamples={getLeaderboardSamples}>
            <p className="token-10 text-glass-muted uppercase">Monthly Prestige</p>
          </MetricInfoIcon>
          <div className="flex items-center gap-2 mt-1">
            <span className="v2-secondary-number text-glass">{personalStats.monthlyPrestige}</span>
            {userPrestige && userPrestige.current_level >= 0 && (
              <PrestigeBadge
                level={userPrestige.current_level}
                highestAchieved={userPrestige.highest_level_achieved}
                size="sm"
                showStars={false}
              />
            )}
          </div>
          {/* Month progress */}
          <div className="mt-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1 rounded-full bg-black/[0.06] overflow-hidden">
                <div
                  className="h-full rounded-full bg-ds-purple"
                  style={{
                    width: `${Math.max(5, Math.round(((30 - personalStats.daysLeft) / 30) * 100))}%`,
                  }}
                />
              </div>
              <span className="token-10 tabular-nums text-glass-muted">
                {personalStats.daysLeft}d left
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabbed Sector Card */}
      <Card className="rounded-[20px] overflow-hidden">
        {/* Tab bar + date label — NEW DEFECT (2026-07-14 responsive pass): with no wrap/scroll,
            "Dialers" got silently clipped by the Card's own overflow-hidden at mobile widths
            (never bubbled up as page overflow, so the original sweep missed it). Tabs get their
            own horizontal scroll; date label stacks below on narrow viewports. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div className="flex items-center gap-1 overflow-x-auto">
            {availableSectors.map((sector) => {
              const count = sectorEntries[sector]?.length || 0;
              const isActive = sector === activeSector;
              return (
                <button
                  key={sector}
                  onClick={() => handleSectorChange(sector)}
                  className={`px-4 py-2 token-14s rounded-lg transition-all whitespace-nowrap shrink-0 ${
                    isActive
                      ? "bg-black/[0.07] text-glass shadow-sm"
                      : "text-glass-muted hover:text-glass-secondary hover:bg-black/[0.03]"
                  }`}
                >
                  {SECTOR_LABELS[sector]}
                  {count > 0 && (
                    <span
                      className={`ml-1.5 font-mono tabular-nums ${isActive ? "text-glass-secondary" : "text-glass-inactive"}`}
                    >
                      ({count})
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            {buildHeaderLabel(activeSector) && (
              <span className="token-10 text-glass-muted uppercase">
                {buildHeaderLabel(activeSector)}
              </span>
            )}
            <span className="token-13m text-glass-muted" data-testid="leaderboard-date-label">
              {dateLabel}
            </span>
          </div>
        </div>

        {/* Animated sector content */}
        <div ref={sectorContentRef} className="overflow-hidden">
          <div
            className={
              slideDirection === "left"
                ? "lb-slide-left"
                : slideDirection === "right"
                  ? "lb-slide-right"
                  : ""
            }
          >
            <SectorTable
              sector={activeSector}
              entries={sectorEntries[activeSector] || []}
              userId={effectiveUserId}
              isUserSector={activeSector === userSector}
              getSamples={getLeaderboardSamples}
              prestigeRanks={prestigeRanks}
              receiptsMap={receiptsMap}
              dailyRows={activeLeaderboard}
              timezone={orgTimezone}
              cohortOrgId={viewTab === "company" ? null : currentOrganization?.id || null}
              cohortPeriodStart={cohortPeriodStart}
              dialsById={dialsById}
            />
          </div>
        </div>
      </Card>

      {/* Monthly Prestige Standings — company-wide view only */}
      {viewTab === "company" && prestigeStandings.length > 0 && (
        <Card className="rounded-[20px] anim-lift">
          <div className="flex items-center justify-between mb-4">
            <h3 className="token-14s text-glass">
              {new Date().toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
                timeZone: orgTimezone,
              })}{" "}
              Standings
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-black/[0.06] text-left token-10">
                  <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase w-16">
                    Rank
                  </th>
                  <th
                    className="px-6 py-2.5 font-semibold text-glass-muted uppercase sticky left-0 z-10"
                    style={{ background: "var(--glass-background)" }}
                  >
                    Name
                  </th>
                  <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase">Pod</th>
                  {!hasTunnelVision && (
                    <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase">Sector</th>
                  )}
                  <th className="px-6 py-2.5 font-semibold text-glass-muted uppercase text-right">
                    <MetricInfoIcon
                      metricKey="lb.monthlyPrestige"
                      getSamples={getLeaderboardSamples}
                    >
                      Points
                    </MetricInfoIcon>
                  </th>
                </tr>
              </thead>
              <tbody>
                {prestigeStandings.map((entry, i) => {
                  const isCurrentUser = entry.rep_id === effectiveUserId;
                  const rank = i + 1;
                  const tierColor =
                    rank === 1
                      ? RANK_COLORS.gold
                      : rank === 2
                        ? RANK_COLORS.silver
                        : rank === 3
                          ? RANK_COLORS.bronze
                          : null;
                  const zebraBg = !tierColor && rank % 2 === 0 ? "rgba(0,0,0,0.025)" : undefined;
                  const rowBg = isCurrentUser
                    ? "rgba(0,0,0,0.04)"
                    : tierColor
                      ? tierColor.bg
                      : zebraBg;

                  return (
                    <tr
                      key={entry.rep_id}
                      className="border-b border-black/[0.04] hover:bg-black/[0.03] transition-colors anim-pop"
                      style={{
                        backgroundColor: isCurrentUser
                          ? "rgba(0,0,0,0.04)"
                          : tierColor
                            ? tierColor.bg
                            : zebraBg,
                        borderLeft: tierColor
                          ? `2px solid ${tierColor.accent}`
                          : "2px solid transparent",
                        animationDelay: `${Math.min(i, 15) * 100}ms`,
                        animationDuration: "480ms",
                      }}
                    >
                      <td className="px-6 py-3">
                        <RankBadge rank={rank} />
                      </td>
                      <td
                        className={`px-6 py-3 text-glass sticky left-0 z-10 ${rank <= 3 ? "token-14 font-semibold" : "token-13"}`}
                        style={{ background: rowBg ?? "var(--glass-background)" }}
                      >
                        <div className="flex items-center gap-2">
                          {(() => {
                            const pr = prestigeRanks.find((r) => r.rep_id === entry.rep_id);
                            return pr && pr.current_level >= 0 ? (
                              <PrestigeBadge
                                level={pr.current_level}
                                highestAchieved={pr.highest_level_achieved}
                                size="sm"
                                showStars={false}
                              />
                            ) : null;
                          })()}
                          <span>
                            {entry.rep_name}
                            {isCurrentUser && (
                              <span className="ml-2 token-11 text-glass-muted font-normal">
                                (You)
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-glass-secondary token-13">
                        <div className="flex items-center gap-1.5">
                          {entry.pod_id && (
                            <span
                              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                              style={{
                                backgroundColor:
                                  allPods.find((p) => p.id === entry.pod_id)?.color || HEX.accent,
                              }}
                            />
                          )}
                          {entry.pod_name || "—"}
                        </div>
                      </td>
                      {!hasTunnelVision && (
                        <td className="px-6 py-3 text-glass-muted capitalize token-13">
                          {entry.sector}
                        </td>
                      )}
                      <td className="px-6 py-3 text-right">
                        <span className="token-15 font-bold text-glass">
                          {entry.total_points || 0}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {/* Exec controls — Updated timestamp, refresh, MVP, calculations (moved below leaderboard 2026-05-01) */}
      {isRealExec && !hasTunnelVision && (
        <div className="flex items-center justify-end gap-2 pt-1">
          {isRealtimeConnected && (
            <span
              data-testid="leaderboard-realtime-indicator"
              className="flex items-center gap-1 text-xs text-ds-green"
            >
              <span className="w-1.5 h-1.5 bg-ds-green rounded-full animate-pulse" />
              Live
            </span>
          )}
          {latestDate && (
            <span className="token-10 text-glass-muted">
              Updated {formatDateKeyDisplay(latestDate, orgTimezone)}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="anim-tap p-1.5 text-glass-muted hover:text-glass-secondary transition-colors disabled:opacity-50"
            title="Refresh leaderboard"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
          <button
            onClick={() => setShowMvpVoting(true)}
            className="anim-tap p-1.5 text-glass-muted hover:text-glass-secondary transition-colors"
            title="MVP Voting"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </button>
          <button
            onClick={() => setShowMvpAdmin(true)}
            className="anim-tap p-1.5 text-glass-muted hover:text-glass-secondary transition-colors"
            title="View vote tallies"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
              />
            </svg>
          </button>
          <button
            onClick={() => setShowCalculations(true)}
            className="anim-tap p-1.5 text-glass-muted hover:text-glass-secondary transition-colors"
            title="How scores are calculated"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Calculations Drawer — exec only */}
      {isRealExec && (
        <CalculationsDrawer
          isOpen={showCalculations}
          onClose={() => setShowCalculations(false)}
          activeSector={activeSector}
          sectorEntries={sectorEntries}
          activeLeaderboard={activeLeaderboard}
          dateLabel={dateLabel}
          timezone={orgTimezone}
        />
      )}

      {/* MVP Voting Modal */}
      {mvpStatus && (
        <MvpVotingModal
          isOpen={showMvpVoting}
          onClose={() => setShowMvpVoting(false)}
          teammates={mvpTeammates}
          myVote={mvpMyVote}
          castVote={mvpCastVote}
          votingOpen={mvpStatus.voting_open}
          daysUntilOpen={mvpStatus.days_until_open}
          monthLabel={mvpMonthLabel}
        />
      )}

      {/* MVP Admin Modal — exec/manager only */}
      {canManageTeam && mvpStatus && (
        <MvpAdminModal
          isOpen={showMvpAdmin}
          onClose={() => setShowMvpAdmin(false)}
          votingStatus={mvpStatus}
          teammates={mvpTeammates}
          monthLabel={mvpMonthLabel}
        />
      )}
    </div>
  );
};

export default LeaderboardPage;
